import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import paccsList from '../data/paccsList.json';

const PAGE_SIZE = 50;

const Paccs = () => {
  const { language } = useLanguage();
  const [showServices, setShowServices] = useState(false);

  // ----- PACCS list: search + filters -----
  const [showList, setShowList] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [regionFilter, setRegionFilter] = useState("");
  const [circleFilter, setCircleFilter] = useState("");
  const [page, setPage] = useState(1);

  // Unique regions (sorted)
  const regions = useMemo(
    () => [...new Set(paccsList.map((p) => p.region).filter(Boolean))].sort(),
    []
  );

  // Circles available for the selected region (or all)
  const circles = useMemo(() => {
    const source = regionFilter
      ? paccsList.filter((p) => p.region === regionFilter)
      : paccsList;
    return [...new Set(source.map((p) => p.circle).filter(Boolean))].sort();
  }, [regionFilter]);

  // Filtered rows
  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return paccsList.filter((p) => {
      if (regionFilter && p.region !== regionFilter) return false;
      if (circleFilter && p.circle !== circleFilter) return false;
      if (term) {
        const hay = `${p.name} ${p.regNo} ${p.region} ${p.circle}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [searchTerm, regionFilter, circleFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const resetPage = () => setPage(1);

  const content = {
    en: {
      title: "Primary Agricultural Co-operative Credit Societies",
      about: "About",
      aboutText: "Primary Agricultural Co-operative Credit Societies are grass – root institutions that address the financial needs of farmers and rural communities through credit and non-credit services.",
      
      coverage: "Coverage",
      coverageList: [
        "4,471 PACCS – Hyperlink District – Circle – Block – Societies",
        "Serving 12,620 Village Panchayats",
        "223 PACCS operate 225 Branches"
      ],
      
      servicesOffered: "Services Offered",
      creditServices: "Credit Services",
      tableHeaders: {
        slNo: "Sl. No.",
        loanType: "Type of the Loan",
        eligibility: "Eligibility for the Loan",
        documents: "Documents Required"
      },
      
      loanTypes: [
        "Crop Loan",
        "Jewel Loan",
        "NFS loan",
        "Produce pledge loan",
        "Animal Husbandry Loan",
        "Medium Term Loan",
        "Self Help Groups Loan",
        "TABCEDCO Loan",
        "TAMCO Loan",
        "TAHDCO Loan",
        "Differently Abled Persons Loan",
        "Housing and House Mortgage Loan"
      ],
      
      g2cServices: "G2C Services",
      g2cList: [
        "Kisan Credit Cards (KCC)",
        "e-Vadagai Services (Renting out Agri Machineries and Implements)",
        "Interest-Free KCC Loans",
        "e-KCC Loans"
      ],
      
      nonCreditServices: "Non-Credit Services",
      nonCreditList: [
        "Fair Price Shops",
        "Pharmacies",
        "Petrol & Diesel Retail Outlets",
        "Agro Service Centres",
        "Agri Clinics"
      ],
      
      otherServices: "Other Services",
      otherServicesList: [
        "Fertilizer Supply",
        "Godown Facilities"
      ],
      
      policyRegulation: "Policy & Regulation",
      policyText: "Administrative control rests with the Registrar of Cooperative Societies, and are governed by the By Laws of the societies.",

      listTitle: "List of PACCS",
      searchPlaceholder: "Search by name / reg. no. ...",
      allRegions: "All Regions",
      allCircles: "All Circles",
      listHeaders: {
        slNo: "S. No.",
        region: "Region",
        circle: "Circle",
        regNo: "Reg. No.",
        name: "Name of the PACCS"
      },
      showingResults: "Showing",
      of: "of",
      results: "PACCS",
      noResults: "No PACCS found.",
      prev: "Previous",
      next: "Next",
      pageLabel: "Page"
    },
    ta: {
      title: "தொடக்க வேளாண்மைக் கூட்டுறவுக் கடன் சங்கங்கள்",
      about: "அறிமுகம்",
      aboutText: "தொடக்க வேளாண்மைக் கூட்டுறவுக் கடன் சங்கங்கள் என்பவை கடன் மற்றும் கடன் அல்லாத சேவைகளை வழங்குவதன்மூலம் உழவர்கள் மற்றும் ஊரகப் பகுதிகளில் வாழும் மக்களுக்கான நிதித்தேவைகளை நிவர்த்தி செய்கின்றன.",

      coverage: "சேவை எல்லை",
      coverageList: [
        "4,471 தொடக்க வேளாண்மைக் கூட்டுறவுக் கடன் சங்கங்கள் – மாவட்ட, சரக, வட்ட சங்கங்கள் வரையில் இணைப்பு",
        "12,620 கிராம பஞ்சாயத்துகளில் சேவை.",
        "223 தொடக்க வேளாண்மைக் கூட்டுறவுக் கடன் சங்கங்கள் 225 கிளைகளை இயக்குகின்றன."
      ],

      servicesOffered: "வழங்கப்படும் சேவைகள்",
      creditServices: "கடன் சேவைகள்",
      tableHeaders: {
        slNo: "வ.எண்.",
        loanType: "சேவை வகை",
        eligibility: "தகுதி",
        documents: "தேவையான ஆவணங்கள்"
      },

      loanTypes: [
        "உழவர் கடன் அட்டைகள்",
        "இணைய வழியிலான வாடகை சேவைகள் (வேளாண் கருவிகள் மற்றும் உபகரணங்களை வாடகைக்குத் தருதல்)",
        "வட்டியில்லாக் கடன்கள்",
        "இணைய வழியிலான வட்டியில்லாக் கடன்கள்"
      ],

      g2cServices: "பொதுமக்களுக்கான அரசின் சேவைகள்",
      g2cList: [
        "உழவர் கடன் அட்டைகள்",
        "இணைய வழியிலான வாடகை சேவைகள் (வேளாண் கருவிகள் மற்றும் உபகரணங்களை வாடகைக்குத் தருதல்)",
        "வட்டியில்லாக் கடன்கள்",
        "இணைய வழியிலான வட்டியில்லாக் கடன்கள்"
      ],

      nonCreditServices: "கடன் அல்லாத சேவைகள்",
      nonCreditList: [
        "நியாயவிலைக் கடைகள்",
        "மருந்தகங்கள்",
        "பெட்ரோல் / டீசல் சில்லறை விற்பனை மையங்கள்",
        "வேளாண் சேவை மையங்கள்"
      ],

      otherServices: "பிற சேவைகள்",
      otherServicesList: [
        "உர விநியோகம்",
        "கிடங்கு வசதிகள்"
      ],

      policyRegulation: "கொள்கை மற்றும் ஒழுங்குமுறை",
      policyText: "கூட்டுறவுச் சங்கங்களின் பதிவாளரின் நிர்வாகக் கட்டுப்பாட்டில் இயங்கும் இச்சங்கங்கள், இவற்றின் துணைவிதிகளின் மூலம் நிர்வகிக்கப்படுகின்றன.",

      listTitle: "தொடக்க வேளாண்மைக் கூட்டுறவுக் கடன் சங்கங்களின் பட்டியல்",
      searchPlaceholder: "பெயர் / பதிவு எண் மூலம் தேடுக ...",
      allRegions: "அனைத்து மண்டலங்கள்",
      allCircles: "அனைத்து சரகங்கள்",
      listHeaders: {
        slNo: "வ. எண்.",
        region: "மண்டலம்",
        circle: "சரகம்",
        regNo: "பதிவு எண்.",
        name: "சங்கத்தின் பெயர்"
      },
      showingResults: "காட்டப்படுவது",
      of: "மொத்தம்",
      results: "சங்கங்கள்",
      noResults: "சங்கங்கள் எதுவும் கிடைக்கவில்லை.",
      prev: "முந்தையது",
      next: "அடுத்தது",
      pageLabel: "பக்கம்"
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-gray-50">
      
      {/* Simple Header */}
      <div className="bg-blue-50 border-b border-blue-100">
        <div className="mx-auto px-4 py-10">
          <h1 className="text-3xl font-bold text-gray-800 text-center">
            {t.title}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto px-4 py-8">

        {/* ===== PACCS SECTION ===== */}
        <section id="paccs-section" className="bg-white p-8 rounded-lg shadow-sm">

          {/* ABOUT */}
          <div id="paccs-about" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.about}</h3>
            <div className="bg-blue-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                {t.aboutText}
              </p>
            </div>
          </div>

          {/* COVERAGE */}
          <div id="paccs-coverage" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.coverage}</h3>
            <div className="bg-green-50 p-6 rounded-lg">
              <ul className="space-y-3">
                {t.coverageList.map((item, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 mr-3 flex-shrink-0">•</span>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* SERVICES OFFERED - ON CLICK */}
          <div id="paccs-services" className="mb-10">
            <div className="bg-purple-50 p-6 rounded-lg">
              <div 
                className="cursor-pointer flex items-center justify-between"
                onClick={() => setShowServices(!showServices)}
              >
                <h3 className="text-lg font-semibold text-gray-800">{t.servicesOffered}</h3>
                {showServices ? (
                  <ChevronDown className="text-blue-600" size={24} />
                ) : (
                  <ChevronRight className="text-gray-400" size={24} />
                )}
              </div>

              {showServices && (
                <div className="mt-6">
                  
                  {/* Credit Services - Full Width */}
                  <div>
                    <h4 className="font-semibold text-gray-800 mb-3">{t.creditServices}</h4>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="bg-purple-200">
                              <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.slNo}</th>
                              <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.loanType}</th>
                              <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.eligibility}</th>
                              <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.tableHeaders.documents}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {t.loanTypes.map((loan, index) => (
                              <tr key={index} className="border-b border-gray-300">
                                <td className="p-3 text-gray-800 font-medium border border-gray-300">{index + 1}</td>
                                <td className="p-3 text-gray-700 border border-gray-300">{loan}</td>
                                <td className="p-3 text-gray-500 border border-gray-300">-</td>
                                <td className="p-3 text-gray-500 border border-gray-300">-</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* G2C, Non-Credit, and Other Services - Separate Section */}
          <div id="additional-services" className="mb-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
              {/* G2C Services */}
              <div className="flex flex-col h-full">
                <h4 className="font-semibold text-gray-800 mb-3">{t.g2cServices}</h4>
                <div className="bg-teal-700 p-4 rounded-lg flex-1">
                  <ul className="space-y-2">
                    {t.g2cList.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-teal-200 mr-2 flex-shrink-0">•</span>
                        <span className="text-white text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Non-Credit Services */}
              <div className="flex flex-col h-full">
                <h4 className="font-semibold text-gray-800 mb-3">{t.nonCreditServices}</h4>
                <div className="bg-teal-700 p-4 rounded-lg flex-1">
                  <ul className="space-y-2">
                    {t.nonCreditList.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-teal-200 mr-2 flex-shrink-0">•</span>
                        <span className="text-white text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Other Services */}
              <div className="flex flex-col h-full">
                <h4 className="font-semibold text-gray-800 mb-3">{t.otherServices}</h4>
                <div className="bg-teal-700 p-4 rounded-lg flex-1">
                  <ul className="space-y-2">
                    {t.otherServicesList.map((item, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-teal-200 mr-2 flex-shrink-0">•</span>
                        <span className="text-white text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>
          </div>

          {/* POLICY & REGULATION */}
          <div id="paccs-policy" className="mb-10">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">{t.policyRegulation}</h3>
            <div className="bg-indigo-50 p-6 rounded-lg">
              <p className="text-gray-700 leading-relaxed">
                {t.policyText}
              </p>
            </div>
          </div>

          {/* ===== LIST OF PACCS (searchable + filterable) ===== */}
          <div id="paccs-list">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div
                className="cursor-pointer flex items-center justify-between"
                onClick={() => setShowList(!showList)}
              >
                <h3 className="text-lg font-semibold text-gray-800">
                  {t.listTitle}{" "}
                  <span className="text-sm font-normal text-gray-500">
                    ({paccsList.length})
                  </span>
                </h3>
                {showList ? (
                  <ChevronDown className="text-blue-600" size={24} />
                ) : (
                  <ChevronRight className="text-gray-400" size={24} />
                )}
              </div>

              {showList && (
                <div className="mt-6">
                  {/* Search + Filters */}
                  <div className="flex flex-col md:flex-row gap-3 mb-4">
                    <div className="relative flex-1">
                      <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={18}
                      />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          resetPage();
                        }}
                        placeholder={t.searchPlaceholder}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <select
                      value={regionFilter}
                      onChange={(e) => {
                        setRegionFilter(e.target.value);
                        setCircleFilter("");
                        resetPage();
                      }}
                      className="py-2 px-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t.allRegions}</option>
                      {regions.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>

                    <select
                      value={circleFilter}
                      onChange={(e) => {
                        setCircleFilter(e.target.value);
                        resetPage();
                      }}
                      className="py-2 px-3 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">{t.allCircles}</option>
                      {circles.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Result count */}
                  <p className="text-sm text-gray-500 mb-2">
                    {t.showingResults} {filtered.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1}
                    {"–"}
                    {Math.min(currentPage * PAGE_SIZE, filtered.length)} {t.of}{" "}
                    {filtered.length} {t.results}
                  </p>

                  {/* Table */}
                  <div className="bg-white rounded-lg overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                      <thead>
                        <tr className="bg-blue-100">
                          <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300 whitespace-nowrap">{t.listHeaders.slNo}</th>
                          <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.listHeaders.region}</th>
                          <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.listHeaders.circle}</th>
                          <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.listHeaders.regNo}</th>
                          <th className="p-3 text-left text-gray-800 font-semibold border border-gray-300">{t.listHeaders.name}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="p-6 text-center text-gray-500 border border-gray-300">
                              {t.noResults}
                            </td>
                          </tr>
                        ) : (
                          pageRows.map((p, idx) => (
                            <tr key={`${p.regNo}-${idx}`} className="hover:bg-gray-50">
                              <td className="p-3 text-gray-700 border border-gray-300">
                                {(currentPage - 1) * PAGE_SIZE + idx + 1}
                              </td>
                              <td className="p-3 text-gray-700 border border-gray-300">{p.region}</td>
                              <td className="p-3 text-gray-700 border border-gray-300">{p.circle}</td>
                              <td className="p-3 text-gray-700 border border-gray-300">{p.regNo}</td>
                              <td className="p-3 text-gray-800 font-medium border border-gray-300">{p.name}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 mt-4">
                      <button
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        {t.prev}
                      </button>
                      <span className="text-sm text-gray-600">
                        {t.pageLabel} {currentPage} / {totalPages}
                      </span>
                      <button
                        onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100"
                      >
                        {t.next}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </section>

      </div>
    </div>
  );
};

export default Paccs;